import { useState, useEffect } from "react";
import {
  Card,
  Form,
  Select,
  Button,
  Tooltip,
  Space,
  Switch,
  Col,
  Row,
  Checkbox,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

export default function CounterpartySettingsContent({
  form,
  onNext,
  onPrevious,
  interbankTemplate,
}) {
  const [swiftCode, setSwiftCode] = useState(
    form.getFieldValue("allowSwiftCode")
  );
  const [dealConfirmation, setDealConfirmation] = useState(
    form.getFieldValue("activateDealConfirmation")
  );
  const [realTimeGrossSettlement, setRealTimeGrossSettlement] = useState(
    form.getFieldValue("allowRTGS")
  );

  // Initialize switch states from form values when component mounts or form values change
  useEffect(() => {
    const formValues = form.getFieldsValue([
      "allowSwiftCode",
      "allowRTGS",
      "activateDealConfirmation",
    ]);
    setSwiftCode(formValues.allowSwiftCode);
    setRealTimeGrossSettlement(formValues.allowRTGS);
  }, [form]);

  const handleInputChange = (field, value) => {
    form.setFieldValue(field, value);
  };

  const handleSwiftCodeChange = (checked) => {
    setSwiftCode(checked);
    handleInputChange("allowSwiftCode", checked);
    // If turning on SwiftCode, turn off RealTimeGrossSettlement
    if (checked) {
      setRealTimeGrossSettlement(false);
      handleInputChange("allowRTGS", false);
    }
  };
  const handleDealConfirmationChange = (checked) => {
    setDealConfirmation(checked);
    handleInputChange("activateDealConfirmation", checked);
    // If turning on SwiftCode, turn off RealTimeGrossSettlement
  };

  const handleRealTimeGrossSettlementChange = (checked) => {
    setRealTimeGrossSettlement(checked);
    handleInputChange("allowRTGS", checked);
    // If turning on RealTimeGrossSettlement, turn off SwiftCode
    if (checked) {
      setSwiftCode(false);
      handleInputChange("allowSwiftCode", false);
    }
  };

  const handleNext = async () => {
    try {
      await form.validateFields(["counterpartyTypes", "kycRequirements"]);
      onNext();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Card
      style={{
        borderRadius: "12px",
        boxShadow:
          "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "32px",
        }}
      >
        <h1
          style={{
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "32px",
            color: "#101828",
            marginBottom: "0px",
          }}
        >
          Counterparty Settings
        </h1>
        <p
          style={{
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#344054",
            marginTop: "0px",
          }}
        >
          Set Counterparty Requirements and Operational Preferences.
        </p>
      </div>

      <Form layout="vertical" requiredMark={false} form={form}>
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Counterparty Type
                  <Tooltip title="Counterparty type information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="counterpartyTypes"
              rules={[
                { required: true, message: "Please select counterparty type" },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Select
                placeholder="Select counterparty type"
                mode="multiple"
                style={{ width: "100%" }}
                onChange={(value) =>
                  handleInputChange("counterpartyTypes", value)
                }
                options={interbankTemplate?.counterpartyTypeOptions?.map(
                  (type) => ({
                    value: type.id,
                    label: type.name,
                  })
                )}
                menuItemSelectedIcon={<Checkbox />}
                popupClassName="custom-select-dropdown"
                className="custom-multi-select"
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  KYC Requirements
                  <Tooltip title="KYC requirements information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="kycRequirements"
              rules={[
                { required: true, message: "Please select KYC requirement" },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Select
                placeholder="Select KYC requirement"
                style={{ width: "100%", height: "44px" }}
                onChange={(value) =>
                  handleInputChange("kycRequirements", value)
                }
                options={interbankTemplate?.kycRequirementOptions?.map(
                  (requirement) => ({
                    value: requirement.id,
                    label: requirement.name,
                  })
                )}
                allowClear
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              name="activateDealConfirmation"
              style={{ marginBottom: 16 }}
              valuePropName="checked"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Switch
                  size="large"
                  style={{ backgroundColor: undefined }}
                  checked={dealConfirmation}
                  onChange={handleDealConfirmationChange}
                />
                <Space>Activate Deal Confirmation</Space>
              </div>
            </Form.Item>

            <Form.Item
              name="allowSwiftCode"
              style={{ marginBottom: 16 }}
              valuePropName="checked"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Switch
                  size="large"
                  style={{ backgroundColor: undefined }}
                  checked={swiftCode}
                  onChange={handleSwiftCodeChange}
                />
                <Space>SWIFT Code</Space>
              </div>
            </Form.Item>

            <Form.Item
              name="allowRTGS"
              style={{ marginBottom: 16 }}
              valuePropName="checked"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Switch
                  size="large"
                  style={{ backgroundColor: undefined }}
                  checked={realTimeGrossSettlement}
                  onChange={handleRealTimeGrossSettlementChange}
                />
                <Space>Real Time Gross Settlement (RTGS)</Space>
              </div>
            </Form.Item>
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "32px",
            gap: "12px",
          }}
        >
          <Button style={{ height: "44px" }} onClick={onPrevious}>
            Previous
          </Button>
          <Button
            type="primary"
            style={{ backgroundColor: "#9F1AB1", height: "44px" }}
            onClick={handleNext}
          >
            Next - Compliance
          </Button>
        </div>
      </Form>
    </Card>
  );
}
